<template>
  <div class="cart-page">
    <MainNavbar />

    <section class="cart-section" :class="{ blurred: showModal }">
      <div class="cart-header">
        <div class="cart-header-top">
          <div class="cart-header-text">
            <h1 class="cart-heading">
              <i class="fa-solid fa-cart-shopping"></i>
              Your Shopping Cart
            </h1>
            <p v-if="cartItems.length" class="cart-subtitle">
              {{ totalItems }} {{ totalItems === 1 ? 'item' : 'items' }} in your cart
            </p>
          </div>
          <button
            type="button"
            class="my-orders-btn link-btn link-btn--primary"
            @click="goToMyOrders"
            aria-label="View your placed orders"
          >
            <i class="fa-solid fa-box-open" aria-hidden="true"></i>
            My Orders
          </button>
        </div>
      </div>

      <div class="cart-container" v-if="cartItems.length">
        <div
          v-for="(item, index) in cartItems"
          :key="cartLineKey(item)"
          class="cart-item"
          :style="{ animationDelay: `${index * 0.05}s` }"
        >
          <div class="product-img-wrapper">
            <img
              :src="getImageUrl(item.thumbnail)"
              :alt="item.name"
              class="product-img"
              loading="lazy"
              decoding="async"
              @error="handleImageError"
            />
            <div
              v-if="item.discounted && cartStore.getItemPrice(item) < Number(item.price)"
              class="img-discount-badge"
            >
              <template v-if="itemDiscountPercent(item) != null">{{ itemDiscountPercent(item) }}% OFF</template>
              <template v-else>Sale</template>
            </div>
          </div>

          <div class="product-info">
            <h3 class="product-name">{{ item.name }}</h3>
            <p v-if="item.color_label || item.color_id" class="product-color-meta">
              <span
                v-if="item.color_primary || item.color_secondary"
                class="product-color-swatch"
                aria-hidden="true"
              >
                <span
                  class="product-color-swatch__half"
                  :style="{ background: '#' + (item.color_primary || 'ccc') }"
                ></span>
                <span
                  class="product-color-swatch__half"
                  :style="{ background: '#' + (item.color_secondary || item.color_primary || 'ccc') }"
                ></span>
              </span>
              Color: {{ item.color_label || item.color_id }}
            </p>
            <p class="description">{{ truncateDescription(item.description) }}</p>
            
            <!-- Price Display with Discount -->
            <div class="price-display">
              <div
                v-if="item.discounted && cartStore.getItemPrice(item) < Number(item.price)"
                class="discounted-price"
              >
                <div class="price-row-main">
                  <span class="original-price crossed">
                    Ksh {{ item.price.toLocaleString() }}
                  </span>
                  <span class="final-price highlight">
                    Ksh {{ cartStore.getItemPrice(item).toLocaleString() }}
                  </span>
                </div>
                <span
                  v-if="itemDiscountPercent(item) != null"
                  class="discount-badge"
                >
                  <i class="fa-solid fa-tag"></i>
                  {{ itemDiscountPercent(item) }}% OFF
                </span>
              </div>
              <div v-else class="regular-price">
                <span class="normal-price">
                  Ksh {{ item.price.toLocaleString() }}
                </span>
              </div>
            </div>

            <div class="quantity-wrapper">
              <label class="quantity-label">Quantity:</label>
              <div class="quantity">
                <button 
                  @click="decreaseQty(item)" 
                  :disabled="item.quantity <= 1"
                  class="qty-btn qty-decrease"
                  aria-label="Decrease quantity"
                >
                  <i class="fa-solid fa-minus"></i>
                </button>
                <span class="qty-value">{{ item.quantity }}</span>
                <button 
                  @click="increaseQty(item)"
                  class="qty-btn qty-increase"
                  aria-label="Increase quantity"
                >
                  <i class="fa-solid fa-plus"></i>
                </button>
              </div>
            </div>

            <div v-if="itemNeedsSize(item)" class="size-picker-block">
              <p class="size-picker-block__title">
                <i class="fa-solid fa-ruler-horizontal" aria-hidden="true" />
                {{ sizeFieldLabel(item) }} (one per item)
              </p>
              <div
                v-for="unitIndex in Number(item.quantity)"
                :key="`${cartLineKey(item)}-size-${unitIndex}`"
                class="size-picker-row"
              >
                <label :for="sizeControlId(item, unitIndex)" class="size-picker-row__label">
                  {{ isFootwearItem(item) ? `Pair ${unitIndex}` : `Item ${unitIndex}` }}
                </label>
                <select
                  :id="sizeControlId(item, unitIndex)"
                  class="size-picker-row__select"
                  :value="item.sizes?.[unitIndex - 1] || ''"
                  @change="updateItemSize(item, unitIndex - 1, $event.target.value)"
                >
                  <option value="" disabled>Select size</option>
                  <option
                    v-for="option in sizeOptionsForItem(item)"
                    :key="`${item.item_id}-${unitIndex}-${option}`"
                    :value="option"
                  >
                    {{ option }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <div class="item-total-section">
            <div class="total-price">
              <p class="total-label">Item Total:</p>
              <p class="total">
                Ksh {{ cartStore.getItemTotal(item).toLocaleString() }}
              </p>
              <p
                v-if="item.discounted && cartStore.getItemSavings(item) > 0"
                class="savings"
              >
                <i class="fa-solid fa-piggy-bank"></i>
                Save Ksh {{ cartStore.getItemSavings(item).toLocaleString() }}
              </p>
            </div>
            <button 
              class="remove-item" 
              @click="removeItem(item)" 
              aria-label="Remove item"
              title="Remove from cart"
            >
              <i class="fa-solid fa-trash"></i>
              <span class="remove-text">Remove</span>
            </button>
          </div>
        </div>
      </div>

      <div v-else class="empty-cart">
        <div class="empty-cart-content">
          <div class="empty-cart-icon-wrapper">
            <i class="fa-solid fa-cart-shopping empty-icon"></i>
            <div class="empty-cart-pulse"></div>
          </div>
          <h2 class="empty-cart-title">Your cart is empty</h2>
          <p class="empty-cart-message">Looks like you haven't added anything to your cart yet. Start shopping to fill it up!</p>
          <router-link to="/ipa/store" class="browse-btn" aria-label="Browse products in Ipastore">
            <i class="fa-solid fa-store" aria-hidden="true"></i>
            <span>Browse Products</span>
          </router-link>
        </div>
      </div>

      <div v-if="cartItems.length" class="order-section">
        <div class="order-summary">
          <h2>Order Summary</h2>
          
          <!-- Original Subtotal -->
          <div class="summary-item">
            <span>Subtotal ({{ totalItems }} items)</span>
            <span>Ksh {{ cartStore.cartSubtotal.toLocaleString() }}</span>
          </div>
          
          <!-- Savings -->
          <div v-if="cartStore.totalSavings > 0" class="summary-item savings">
            <span>
              <i class="fa-solid fa-tag"></i>
              Discount Savings
            </span>
            <span class="savings-amount">
              - Ksh {{ cartStore.totalSavings.toLocaleString() }}
            </span>
          </div>
          
          <!-- Final Subtotal after discounts -->
          <div class="summary-item final-subtotal">
            <span><strong>Total After Discount</strong></span>
            <span><strong>Ksh {{ cartStore.cartTotal.toLocaleString() }}</strong></span>
          </div>
          
          <div class="summary-item delivery-info">
            <span>
              <i class="fa-solid fa-truck"></i>
              Delivery
            </span>
            <span>
              <span v-if="delivery.isWithinCBD" class="delivery-badge free">
                <i class="fa-solid fa-check-circle"></i>
                Free (Nairobi CBD)
              </span>
              <span v-else class="delivery-badge charged">
                <i class="fa-solid fa-info-circle"></i>
                Ksh {{ deliveryFee.toLocaleString() }}
              </span>
            </span>
          </div>
          
          <div class="summary-item">
            <span>
              <i class="fa-solid fa-receipt"></i>
              VAT
            </span>
            <span>Inclusive</span>
          </div>
          
          <hr class="summary-divider" />
          
          <div class="summary-total">
            <div class="total-left">
              <strong>Grand Total</strong>
              <small class="total-note">Including delivery</small>
            </div>
            <strong class="total-amount">Ksh {{ total.toLocaleString() }}</strong>
          </div>
          
          <div class="payment-note">
            <i class="fa-solid fa-money-bill-wave"></i>
            <div class="payment-note-content">
              <strong>Payment on Delivery</strong>
              <span>Cash payment when your order arrives</span>
            </div>
          </div>
        </div>

        <div class="delivery-form">
          <h2>Delivery Information</h2>
          <form @submit.prevent="showConfirmation">
            <div class="form-group">
              <label for="address">
                <i class="fa-solid fa-map-marker-alt"></i>
                Delivery Address *
              </label>
              <input 
                id="address"
                type="text" 
                placeholder="Street, Building, Apartment" 
                v-model="delivery.address" 
                required
                :class="{ 'input-error': formErrors.address }"
                @blur="validateField('address')"
                @input="clearFieldError('address')"
              />
              <small v-if="formErrors.address" class="error-message">
                <i class="fa-solid fa-exclamation-circle"></i>
                {{ formErrors.address }}
              </small>
            </div>
            
            <div class="form-group">
              <label for="city">
                <i class="fa-solid fa-city"></i>
                City/Town *
              </label>
              <input 
                id="city"
                type="text" 
                placeholder="e.g., Nairobi CBD, Kilimani, Parklands" 
                v-model="delivery.city" 
                required
                :class="{ 'input-error': formErrors.city }"
                @input="checkCBDStatus(); clearFieldError('city')"
                @blur="validateField('city')"
              />
              <small class="city-hint" :class="{ 'fee-applies': !delivery.isWithinCBD, 'free-delivery': delivery.isWithinCBD }">
                <i :class="delivery.isWithinCBD ? 'fa-solid fa-check-circle' : 'fa-solid fa-info-circle'"></i>
                {{ delivery.isWithinCBD ? 'Free delivery within Nairobi CBD' : 'Delivery fee applies outside Nairobi CBD' }}
              </small>
              <small v-if="formErrors.city" class="error-message">
                <i class="fa-solid fa-exclamation-circle"></i>
                {{ formErrors.city }}
              </small>
            </div>
            
            <div class="form-group location-help-group">
              <span id="locationHelpLabel" class="location-help-label">
                <i class="fa-solid fa-location-dot" aria-hidden="true"></i>
                Exact location (optional)
              </span>
              <small class="field-hint location-help-intro">
                <i class="fa-solid fa-lightbulb" aria-hidden="true"></i>
                Help the rider find you. Paste a Google Maps pin, or share where you are now.
              </small>

              <div
                class="location-mode-toggle"
                role="tablist"
                aria-labelledby="locationHelpLabel"
              >
                <button
                  type="button"
                  class="location-mode-btn"
                  :class="{ active: locationMode === 'pin' }"
                  role="tab"
                  :aria-selected="locationMode === 'pin'"
                  @click="setLocationMode('pin')"
                >
                  <i class="fa-solid fa-map-pin" aria-hidden="true"></i>
                  Map pin
                </button>
                <button
                  type="button"
                  class="location-mode-btn"
                  :class="{ active: locationMode === 'live' }"
                  role="tab"
                  :aria-selected="locationMode === 'live'"
                  @click="setLocationMode('live')"
                >
                  <i class="fa-solid fa-location-crosshairs" aria-hidden="true"></i>
                  Live location
                </button>
              </div>

              <div v-if="locationMode === 'pin'" class="location-mode-panel">
                <label for="pinLocation" class="sr-only">Google Maps pin URL</label>
                <input
                  id="pinLocation"
                  type="url"
                  placeholder="https://maps.google.com/..."
                  v-model="delivery.pinLocation"
                  :class="{ 'input-error': formErrors.pinLocation }"
                  @blur="validateField('pinLocation')"
                  @input="onPinLocationInput"
                />
                <small class="field-hint">
                  Open Google Maps, drop a pin, then paste the share link here
                </small>
              </div>

              <div v-else class="location-mode-panel live-location-panel">
                <p class="live-location-copy">
                  We will save a snapshot of your current position as a map pin for this delivery, not ongoing tracking.
                </p>
                <small
                  v-if="delivery.pinLocation && locationSource !== 'live'"
                  class="field-hint"
                >
                  Sharing live location will replace the map pin you already entered.
                </small>

                <div v-if="liveLocationCaptured" class="live-location-captured">
                  <div class="live-location-captured__status">
                    <i class="fa-solid fa-circle-check" aria-hidden="true"></i>
                    <div>
                      <strong>Current location captured</strong>
                      <span v-if="formattedLiveCoords">{{ formattedLiveCoords }}</span>
                      <span v-if="formattedLiveAccuracy">{{ formattedLiveAccuracy }}</span>
                    </div>
                  </div>
                  <div class="live-location-actions">
                    <a
                      :href="delivery.pinLocation"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="pin-link"
                    >
                      <i class="fa-solid fa-map-pin" aria-hidden="true"></i>
                      View on Maps
                    </a>
                    <button type="button" class="location-text-btn" @click="clearLiveLocation">
                      Clear
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  class="live-location-btn"
                  :disabled="liveLocationLoading"
                  @click="captureLiveLocation"
                >
                  <i
                    :class="liveLocationLoading ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-location-crosshairs'"
                    aria-hidden="true"
                  ></i>
                  {{ liveLocationButtonLabel }}
                </button>
                <small v-if="liveLocationError" class="error-message">
                  <i class="fa-solid fa-exclamation-circle" aria-hidden="true"></i>
                  {{ liveLocationError }}
                </small>
              </div>

              <small v-if="formErrors.pinLocation" class="error-message">
                <i class="fa-solid fa-exclamation-circle"></i>
                {{ formErrors.pinLocation }}
              </small>
            </div>
            
            <div class="form-group">
              <label for="notes">
                <i class="fa-solid fa-sticky-note"></i>
                Additional Notes (Optional)
              </label>
              <textarea 
                id="notes"
                placeholder="Delivery instructions, gate code, etc." 
                rows="3" 
                v-model="delivery.notes"
                maxlength="500"
              ></textarea>
              <small class="field-hint">
                <span class="char-count">{{ delivery.notes.length }}/500</span>
              </small>
            </div>
            
            <button class="checkout-btn" type="submit" :disabled="isProcessing">
              {{ isProcessing ? "Processing Order..." : "Check oUt" }}
            </button>
          </form>
        </div>
      </div>
    </section>

    <!-- CONFIRMATION MODAL -->
    <transition name="fade">
      <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
        <transition name="scale">
          <div class="confirm-modal">
            <!-- Order Success State -->
            <div v-if="orderStatus === 'success'" class="order-success">
              <div class="success-icon">
                <i class="fa-solid fa-circle-check"></i>
              </div>
              <h2>Order Placed Successfully!</h2>
              <div class="success-message">
                <p><strong>Thank you for your order!</strong></p>
                <p>Our team has begun processing your order.</p>
                <p>You will receive an email confirmation shortly.</p>
              </div>
              <div class="delivery-time">
                <i class="fa-solid fa-clock"></i>
                Estimated Delivery: 12-24 hours
              </div>
              <button class="continue-btn" @click="closeModalAndRedirect">
                Continue Shopping
              </button>
            </div>

            <!-- Order Failed State -->
            <div v-else-if="orderStatus === 'error'" class="order-error">
              <div class="error-icon">
                <i class="fa-solid fa-circle-exclamation"></i>
              </div>
              <h2>Order Processing Failed</h2>
              <div class="error-message">
                <p><strong>We couldn't process your order.</strong></p>
                <p>{{ errorMessage }}</p>
                <p>Please try again or contact support if the issue persists.</p>
              </div>
              <div class="error-actions">
                <button class="retry-btn" @click="retryOrder">
                  <i class="fa-solid fa-rotate"></i> Try Again
                </button>
                <button class="cancel-btn" @click="closeModal">
                  Cancel
                </button>
              </div>
            </div>

            <!-- Order Review State -->
            <div v-else class="order-review">
              <div class="confirm-header">
                <h2><i class="fa-solid fa-clipboard-check"></i> Review Your Order</h2>
                <p class="subtitle">Please verify your order details before confirming</p>
              </div>

              <!-- Scrollable content container -->
              <div class="scrollable-content">
                <!-- Order Items -->
                <div class="confirm-section">
                  <h3><i class="fa-solid fa-box"></i> Order Items</h3>
                  <div class="items-list">
                    <div v-for="item in cartItems" :key="cartLineKey(item)" class="item-row">
                      <div class="item-info">
                        <span class="item-name">{{ item.name }}</span>
                        <span
                          v-if="item.color_label || item.color_id"
                          class="item-color-meta"
                        >{{ item.color_label || item.color_id }}</span>
                        <span class="item-quantity">×{{ item.quantity }}</span>
                        <span v-if="formatItemSizesSummary(item)" class="item-size-summary">
                          {{ formatItemSizesSummary(item) }}
                        </span>
                      </div>
                      <div class="item-pricing">
                        <div
                          v-if="item.discounted && cartStore.getItemPrice(item) < Number(item.price)"
                          class="discounted-price-row"
                        >
                          <div class="price-details">
                            <span class="original-price-small">Ksh {{ item.price.toLocaleString() }}</span>
                            <span class="final-price-small">Ksh {{ cartStore.getItemPrice(item).toLocaleString() }}</span>
                          </div>
                          <span
                            v-if="itemDiscountPercent(item) != null"
                            class="item-savings-badge"
                          >Save {{ itemDiscountPercent(item) }}%</span>
                        </div>
                        <div v-else class="regular-price-row">
                          <span class="item-price">Ksh {{ item.price.toLocaleString() }}</span>
                        </div>
                        <span class="item-total">Ksh {{ cartStore.getItemTotal(item).toLocaleString() }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Delivery Details -->
                <div class="confirm-section">
                  <h3><i class="fa-solid fa-location-dot"></i> Delivery Details</h3>
                  <div class="delivery-review compact">
                    <div class="detail-row">
                      <span class="detail-label">Name:</span>
                      <span class="detail-value">{{ getUserName() }}</span>
                    </div>
                    <div class="detail-row">
                      <span class="detail-label">Phone:</span>
                      <span class="detail-value">{{ getUserPhone() }}</span>
                    </div>
                    <div class="detail-row">
                      <span class="detail-label">Address:</span>
                      <span class="detail-value">{{ delivery.address }}, {{ delivery.city }}</span>
                    </div>
                    <div v-if="delivery.pinLocation" class="detail-row">
                      <span class="detail-label">{{ locationReviewLabel }}:</span>
                      <span class="detail-value">
                        <a :href="delivery.pinLocation" target="_blank" rel="noopener noreferrer" class="pin-link">
                          <i class="fa-solid fa-map-pin"></i> View Location
                        </a>
                      </span>
                    </div>
                    <div v-if="delivery.notes" class="detail-row">
                      <span class="detail-label">Notes:</span>
                      <span class="detail-value notes">{{ delivery.notes }}</span>
                    </div>
                  </div>
                </div>

                <!-- Order Summary -->
                <div class="confirm-pricing">
                  <h3><i class="fa-solid fa-receipt"></i> Order Summary</h3>
                  <div class="price-summary">
                    <div class="price-row">
                      <span>Subtotal</span>
                      <span>Ksh {{ cartStore.cartSubtotal.toLocaleString() }}</span>
                    </div>
                    <div v-if="cartStore.totalSavings > 0" class="price-row savings">
                      <span>Discount Savings</span>
                      <span class="savings-text">- Ksh {{ cartStore.totalSavings.toLocaleString() }}</span>
                    </div>
                    <div class="price-row after-discount">
                      <span>Total After Discount</span>
                      <span>Ksh {{ cartStore.cartTotal.toLocaleString() }}</span>
                    </div>
                    <div class="price-row">
                      <span>Delivery</span>
                      <span>{{ delivery.isWithinCBD ? 'Free' : `Ksh ${deliveryFee.toLocaleString()}` }}</span>
                    </div>
                    <div class="price-row total">
                      <strong>Total Amount</strong>
                      <strong>Ksh {{ total.toLocaleString() }}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Fixed Actions -->
              <div class="confirm-actions">
                <button class="c-btn cancel" @click="closeModal" :disabled="isProcessing">
                  <i class="fa-solid fa-arrow-left"></i> Back
                </button>
                <button class="c-btn confirm" @click="finalizeOrder" :disabled="isProcessing">
                  <i class="fa-solid fa-check"></i> Confirm Order
                </button>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </transition>
    <MainFooter />
  </div>
</template>

<script>
import MainNavbar from "../../components/main/headernfooter/header.vue";
import { useCartStore } from "../../cartstore/cartStore";
import { cartLineKeyFromItem } from "../../cartstore/colorVariants";
import { useAuthStore } from "../../authen/auth"
import generateHmacSignature from "@/utils/utils";
import { buildApiUrl } from "@/config/api";
import MainFooter from "../../components/main/headernfooter/Footer.vue";
import globalToastMixin from "@/mixins/globalToastMixin";
import {
  ensureItemSizeSlots,
  formatItemSizesSummary,
  isFootwearItem,
  itemNeedsSize,
  itemSizesComplete,
  sizeFieldLabel,
  sizeOptionsForItem,
} from "@/utils/orderSizes";
import { buildGoogleMapsCoordsUrl } from "@/utils/googleMapsUrl";

export default {
  name: "CartSection",
  mixins: [globalToastMixin],
  components: { MainNavbar, MainFooter },
  setup() {
    const cartStore = useCartStore();
    const authStore = useAuthStore();
    return { cartStore, authStore };
  },

  data() {
    return {
      orderStatus: null,
      errorMessage: "",
      isProcessing: false,
      baseURL: "@/assets/ipalogo.png",
      showModal: false,
      // Delivery form data
      delivery: {
        address: "",
        city: "Nairobi CBD",
        pinLocation: "",
        notes: "",
        isWithinCBD: true
      },
      deliveryFee: 250,
      cbdAreas: ["nairobi cbd", "westlands", "kilimani", "parklands", "upper hill", "kileleshwa"],
      formErrors: {},
      locationMode: "pin",
      locationSource: null,
      liveCoords: null,
      liveLocationLoading: false,
      liveLocationError: "",
    };
  },

  computed: {
    cartItems() {
      return this.cartStore.items;
    },
    totalItems() {
      return this.cartStore.cartCount;
    },
    subtotal() {
      return this.cartStore.cartSubtotal; // Original subtotal before discounts
    },
    total() {
      return this.cartStore.cartTotal + (this.delivery.isWithinCBD ? 0 : this.deliveryFee);
    },
    discountSavings() {
      return this.cartStore.totalSavings;
    },
    liveLocationCaptured() {
      return this.locationSource === "live" && Boolean(this.delivery.pinLocation);
    },
    liveLocationButtonLabel() {
      if (this.liveLocationLoading) return "Getting your location…";
      return this.liveLocationCaptured ? "Update live location" : "Share my current location";
    },
    formattedLiveCoords() {
      if (!this.liveCoords) return "";
      return `${this.liveCoords.latitude.toFixed(5)}, ${this.liveCoords.longitude.toFixed(5)}`;
    },
    formattedLiveAccuracy() {
      if (this.liveCoords?.accuracy == null || Number.isNaN(this.liveCoords.accuracy)) return "";
      const meters = Math.round(this.liveCoords.accuracy);
      if (meters < 1000) return `Accurate to about ${meters} m`;
      return `Accurate to about ${(meters / 1000).toFixed(1)} km`;
    },
    locationReviewLabel() {
      return this.locationSource === "live" ? "Live location" : "PIN Location";
    },
  },

  created() {
    this.cartStore.syncAllItemSizeSlots();
    this.cartStore.saveCart();
  },

  beforeUnmount() {
    this._geoCancelled = true;
  },

  methods: {
    itemNeedsSize,
    isFootwearItem,
    sizeOptionsForItem,
    sizeFieldLabel,
    formatItemSizesSummary,

    updateItemSize(item, unitIndex, value) {
      this.cartStore.setItemSize(item, unitIndex, value);
    },

    cartLineKey(item) {
      return cartLineKeyFromItem(item) || String(item?.item_id ?? "");
    },

    sizeControlId(item, unitIndex) {
      const key = String(this.cartLineKey(item)).replace(/[^a-zA-Z0-9_-]/g, "-");
      return `size-${key}-${unitIndex}`;
    },

    goToMyOrders() {
      try {
        this.authStore.checkToken();
      } catch {
        // fall through to state check
      }

      if (!this.authStore.isAuthenticated || !this.authStore.user) {
        this.showToastMessage(
          "Login Required",
          "Please sign in to view your orders.",
          "warning"
        );
        this.$router
          .push({
            path: "/ipromiseafrica/sign-in",
            query: { redirect: "/ipromiseafrica/store/my-orders" },
          })
          .catch(() => {});
        return;
      }

      this.$router.push({ name: "MyOrders" }).catch(() => {});
    },

    /** Display % off (matches WishList / store: prefer stored %, else derive from list vs sale). */
    itemDiscountPercent(item) {
      if (!item || !item.discounted) return null;
      const raw = item.discount_percentage;
      if (raw != null && raw !== "") {
        const n = Number(raw);
        if (Number.isFinite(n) && n > 0 && n <= 100) return Math.round(n);
      }
      const list = Number(item.price);
      const sale = this.cartStore.getItemPrice(item);
      if (Number.isFinite(list) && list > 0 && Number.isFinite(sale) && sale < list) {
        return Math.round(100 * (1 - sale / list));
      }
      return null;
    },

    getImageUrl(filename) {
      if (!filename) return this.baseURL;
      if (filename.startsWith("http")) return filename;
      return filename;
    },

    truncateDescription(desc) {
      if (!desc) return '';
      return desc.length > 60 ? desc.substring(0, 60) + '...' : desc;
    },

    checkCBDStatus() {
      const city = this.delivery.city.toLowerCase().trim();
      this.delivery.isWithinCBD = this.cbdAreas.some(area => city.includes(area));
    },

    validateField(fieldName) {
      this.formErrors = { ...this.formErrors };
      
      switch(fieldName) {
        case 'address':
          if (!this.delivery.address.trim()) {
            this.formErrors.address = 'Delivery address is required';
          } else if (this.delivery.address.trim().length < 5) {
            this.formErrors.address = 'Address must be at least 5 characters';
          } else {
            delete this.formErrors.address;
          }
          break;
          
        case 'city':
          if (!this.delivery.city.trim()) {
            this.formErrors.city = 'City/town is required';
          } else if (this.delivery.city.trim().length < 2) {
            this.formErrors.city = 'City/town must be at least 2 characters';
          } else {
            delete this.formErrors.city;
          }
          break;
          
        case 'pinLocation':
          if (this.delivery.pinLocation && this.delivery.pinLocation.trim()) {
            const urlPattern = /^https?:\/\/.+/i;
            if (!urlPattern.test(this.delivery.pinLocation.trim())) {
              this.formErrors.pinLocation = 'Please enter a valid URL (starting with http:// or https://)';
            } else {
              delete this.formErrors.pinLocation;
            }
          } else {
            delete this.formErrors.pinLocation;
          }
          break;
      }
    },

    setLocationMode(mode) {
      this.locationMode = mode;
      this.liveLocationError = "";
    },

    onPinLocationInput() {
      const value = (this.delivery.pinLocation || "").trim();
      this.locationSource = value ? "pin" : null;
      if (!value) this.liveCoords = null;
      this.clearFieldError("pinLocation");
    },

    geolocationErrorMessage(error) {
      const code = error && error.code;
      if (code === 1) {
        return "Location permission was denied. Allow location access in your browser, or paste a map pin instead.";
      }
      if (code === 2) {
        return "We could not determine your location. Check that GPS or Wi-Fi is on, then try again.";
      }
      if (code === 3) {
        return "Location request timed out. Try again, or paste a map pin instead.";
      }
      return "Could not get your live location. You can paste a Google Maps pin instead.";
    },

    captureLiveLocation() {
      if (typeof navigator === "undefined" || !navigator.geolocation) {
        this.liveLocationError = "Live location is not supported on this device or browser. Paste a map pin instead.";
        this.showToastMessage("Location unavailable", this.liveLocationError, "warning");
        return;
      }

      this.liveLocationLoading = true;
      this.liveLocationError = "";
      delete this.formErrors.pinLocation;

      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (this._geoCancelled) return;
          const latitude = position?.coords?.latitude;
          const longitude = position?.coords?.longitude;
          const pinUrl = buildGoogleMapsCoordsUrl(latitude, longitude);
          if (!pinUrl) {
            this.liveLocationLoading = false;
            this.liveLocationError = "Received an invalid position. Please try again.";
            return;
          }

          this.delivery.pinLocation = pinUrl;
          this.liveCoords = {
            latitude,
            longitude,
            accuracy: position.coords.accuracy,
          };
          this.locationSource = "live";
          this.liveLocationLoading = false;
          this.validateField("pinLocation");
          this.showToastMessage(
            "Location captured",
            "Your current location was saved as a map pin for this delivery.",
            "success"
          );
        },
        (error) => {
          if (this._geoCancelled) return;
          this.liveLocationLoading = false;
          this.liveLocationError = this.geolocationErrorMessage(error);
          this.showToastMessage("Could not get location", this.liveLocationError, "warning");
        },
        {
          enableHighAccuracy: true,
          timeout: 20000,
          maximumAge: 0,
        }
      );
    },

    clearLiveLocation() {
      this.liveCoords = null;
      this.locationSource = null;
      this.delivery.pinLocation = "";
      this.liveLocationError = "";
      delete this.formErrors.pinLocation;
    },
    
    clearFieldError(fieldName) {
      if (this.formErrors[fieldName]) {
        this.$set(this.formErrors, fieldName, undefined);
      }
    },
    
    validateForm() {
      // Check if user is authenticated
      if (!this.authStore.isAuthenticated || !this.authStore.user) {
        const event = new CustomEvent("show-toast", {
          detail: { 
            title: "Authentication Required", 
            message: "Please log in to proceed with checkout.",
            type: "warning"
          }
        });
        window.dispatchEvent(event);
        this.$router.push('/ipa/sign-in');
        return false;
      }
      
      // Validate all fields
      this.validateField('address');
      this.validateField('city');
      this.validateField('pinLocation');
      
      // Check if there are any errors
      if (Object.keys(this.formErrors).filter(k => this.formErrors[k]).length > 0) {
        const firstError = Object.values(this.formErrors).find(e => e);
        const event = new CustomEvent("show-toast", {
          detail: { 
            title: "Form Validation", 
            message: firstError,
            type: "warning"
          }
        });
        window.dispatchEvent(event);
        return false;
      }
      
      if (!this.delivery.address.trim()) {
        this.validateField('address');
        const event = new CustomEvent("show-toast", {
          detail: { 
            title: "Missing Information", 
            message: "Please enter your delivery address.",
            type: "warning"
          }
        });
        window.dispatchEvent(event);
        return false;
      }
      
      if (!this.delivery.city.trim()) {
        this.validateField('city');
        const event = new CustomEvent("show-toast", {
          detail: { 
            title: "Missing Information", 
            message: "Please enter your city/town.",
            type: "warning"
          }
        });
        window.dispatchEvent(event);
        return false;
      }

      this.cartStore.syncAllItemSizeSlots();
      const incompleteSizeItem = this.cartItems.find((item) => !itemSizesComplete(item));
      if (incompleteSizeItem) {
        const event = new CustomEvent("show-toast", {
          detail: {
            title: "Select sizes",
            message: `Choose ${sizeFieldLabel(incompleteSizeItem).toLowerCase()} for every unit of "${incompleteSizeItem.name}".`,
            type: "warning",
          },
        });
        window.dispatchEvent(event);
        return false;
      }
      
      return true;
    },

    handleImageError(event) {
      event.target.src = require('@/assets/ipalogo.png');
      event.target.onerror = null;
    },

    showConfirmation() {
      if (!this.validateForm()) {
        return;
      }
      
      this.orderStatus = null;
      this.errorMessage = "";
      this.showModal = true;
    },

    /**
     * Validates order payload data before submission
     * @param {Object} payload - Order payload to validate
     * @returns {Object} - { isValid: boolean, errors: Array<string> }
     */
    validateOrderPayload(payload) {
      const errors = [];

      // Validate customer_name
      if (!payload.customer_name || typeof payload.customer_name !== 'string' || payload.customer_name.trim().length < 2) {
        errors.push('Customer name must be at least 2 characters');
      }
      if (payload.customer_name && payload.customer_name.length > 100) {
        errors.push('Customer name must not exceed 100 characters');
      }

      // Note: customer_phone validation removed - backend will use customer account phone number

      // Validate delivery_address
      if (!payload.delivery_address || typeof payload.delivery_address !== 'string' || payload.delivery_address.trim().length < 5) {
        errors.push('Delivery address must be at least 5 characters');
      }
      if (payload.delivery_address && payload.delivery_address.length > 200) {
        errors.push('Delivery address must not exceed 200 characters');
      }

      // Validate delivery_city
      if (!payload.delivery_city || typeof payload.delivery_city !== 'string' || payload.delivery_city.trim().length < 2) {
        errors.push('Delivery city must be at least 2 characters');
      }
      if (payload.delivery_city && payload.delivery_city.length > 50) {
        errors.push('Delivery city must not exceed 50 characters');
      }

      // Validate pin_location (optional)
      if (payload.pin_location && payload.pin_location !== null) {
        if (typeof payload.pin_location !== 'string') {
          errors.push('PIN location must be a valid URL string');
        } else {
          try {
            const url = new URL(payload.pin_location);
            if (!['http:', 'https:'].includes(url.protocol)) {
              errors.push('PIN location must be a valid HTTP/HTTPS URL');
            }
          } catch (e) {
            errors.push('PIN location must be a valid URL');
          }
        }
        if (payload.pin_location.length > 500) {
          errors.push('PIN location must not exceed 500 characters');
        }
      }

      // Validate delivery_notes (optional)
      if (payload.delivery_notes && payload.delivery_notes.length > 500) {
        errors.push('Delivery notes must not exceed 500 characters');
      }

      // Validate delivery_fee
      if (typeof payload.delivery_fee !== 'number' || payload.delivery_fee < 0 || isNaN(payload.delivery_fee)) {
        errors.push('Delivery fee must be a valid non-negative number');
      }

      // Validate items array
      if (!Array.isArray(payload.items) || payload.items.length === 0) {
        errors.push('Order must contain at least one item');
      } else {
        payload.items.forEach((item, index) => {
          // Validate item_id
          if (item.item_id === undefined || item.item_id === null || item.item_id === '') {
            errors.push(`Item ${index + 1}: Item ID is required`);
          }

          // Validate name
          if (!item.name || typeof item.name !== 'string' || item.name.trim().length < 1) {
            errors.push(`Item ${index + 1}: Name is required`);
          }
          if (item.name && item.name.length > 200) {
            errors.push(`Item ${index + 1}: Name must not exceed 200 characters`);
          }

          // Validate quantity
          if (typeof item.quantity !== 'number' || item.quantity < 1 || !Number.isInteger(item.quantity)) {
            errors.push(`Item ${index + 1}: Quantity must be a positive integer`);
          }
          if (item.quantity > 1000) {
            errors.push(`Item ${index + 1}: Quantity cannot exceed 1000`);
          }

          // Validate price
          if (typeof item.price !== 'number' || item.price <= 0 || isNaN(item.price)) {
            errors.push(`Item ${index + 1}: Price must be a positive number`);
          }
          if (item.price > 10000000) {
            errors.push(`Item ${index + 1}: Price cannot exceed 10,000,000`);
          }

          // Validate final_price
          if (item.final_price !== undefined && item.final_price !== null) {
            if (typeof item.final_price !== 'number' || item.final_price < 0 || isNaN(item.final_price)) {
              errors.push(`Item ${index + 1}: Final price must be a valid non-negative number`);
            }
            // If discounted, final_price should be less than or equal to price
            if (item.discounted && item.final_price > item.price) {
              errors.push(`Item ${index + 1}: Final price cannot exceed original price when discounted`);
            }
          }

          // Validate discount_percentage (optional)
          if (item.discount_percentage !== undefined && item.discount_percentage !== null) {
            if (typeof item.discount_percentage !== 'number' || item.discount_percentage < 0 || item.discount_percentage > 100) {
              errors.push(`Item ${index + 1}: Discount percentage must be between 0 and 100`);
            }
          }

          // Validate discounted (optional)
          if (item.discounted !== undefined && item.discounted !== null && typeof item.discounted !== 'boolean') {
            errors.push(`Item ${index + 1}: Discounted must be a boolean value`);
          }

          if (item.sub_category && typeof item.sub_category !== 'string') {
            errors.push(`Item ${index + 1}: Sub-category must be a string`);
          }

          if (item.sizes !== undefined && item.sizes !== null) {
            if (!Array.isArray(item.sizes)) {
              errors.push(`Item ${index + 1}: Sizes must be an array`);
            } else if (item.sizes.length !== item.quantity) {
              errors.push(`Item ${index + 1}: Provide exactly ${item.quantity} size(s)`);
            } else if (item.sizes.some((sizeValue) => !sizeValue || !String(sizeValue).trim())) {
              errors.push(`Item ${index + 1}: Every unit needs a selected size`);
            }
          }
        });
      }

      // Validate subtotal
      if (typeof payload.subtotal !== 'number' || payload.subtotal < 0 || isNaN(payload.subtotal)) {
        errors.push('Subtotal must be a valid non-negative number');
      }

      // Validate discount_savings (optional)
      if (payload.discount_savings !== undefined && payload.discount_savings !== null) {
        if (typeof payload.discount_savings !== 'number' || payload.discount_savings < 0 || isNaN(payload.discount_savings)) {
          errors.push('Discount savings must be a valid non-negative number');
        }
        // Discount savings should not exceed subtotal
        if (payload.discount_savings > payload.subtotal) {
          errors.push('Discount savings cannot exceed subtotal');
        }
      }

      // Validate total_amount
      if (typeof payload.total_amount !== 'number' || payload.total_amount <= 0 || isNaN(payload.total_amount)) {
        errors.push('Total amount must be a valid positive number');
      }

      // Validate payment_method
      if (!payload.payment_method || typeof payload.payment_method !== 'string') {
        errors.push('Payment method is required');
      }
      const validPaymentMethods = ['cash_on_delivery', 'mpesa', 'card', 'bank_transfer'];
      if (payload.payment_method && !validPaymentMethods.includes(payload.payment_method)) {
        errors.push(`Payment method must be one of: ${validPaymentMethods.join(', ')}`);
      }

      // Validate currency
      if (!payload.currency || typeof payload.currency !== 'string' || payload.currency.length !== 3) {
        errors.push('Currency must be a valid 3-letter code (e.g., KES)');
      }

      // Validate status
      if (!payload.status || typeof payload.status !== 'string') {
        errors.push('Order status is required');
      }
      const validStatuses = ['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'];
      if (payload.status && !validStatuses.includes(payload.status)) {
        errors.push(`Order status must be one of: ${validStatuses.join(', ')}`);
      }

      // Validate total amount calculation
      const calculatedSubtotal = payload.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      const calculatedDiscountSavings = payload.items.reduce((sum, item) => {
        if (item.discounted && item.final_price !== undefined && item.final_price !== null) {
          return sum + ((item.price - item.final_price) * item.quantity);
        }
        return sum;
      }, 0);
      const calculatedTotal = calculatedSubtotal - calculatedDiscountSavings + payload.delivery_fee;

      // Allow small floating point differences (0.01)
      const subtotalDiff = Math.abs(calculatedSubtotal - payload.subtotal);
      if (subtotalDiff > 0.01) {
        errors.push(`Subtotal mismatch: calculated ${calculatedSubtotal.toFixed(2)}, provided ${payload.subtotal.toFixed(2)}`);
      }

      const discountDiff = Math.abs(calculatedDiscountSavings - (payload.discount_savings || 0));
      if (discountDiff > 0.01) {
        errors.push(`Discount savings mismatch: calculated ${calculatedDiscountSavings.toFixed(2)}, provided ${(payload.discount_savings || 0).toFixed(2)}`);
      }

      const totalDiff = Math.abs(calculatedTotal - payload.total_amount);
      if (totalDiff > 0.01) {
        errors.push(`Total amount mismatch: calculated ${calculatedTotal.toFixed(2)}, provided ${payload.total_amount.toFixed(2)}`);
      }

      return {
        isValid: errors.length === 0,
        errors: errors
      };
    },

    async finalizeOrder() {
      this.isProcessing = true;
      
      try {
        if (!this.authStore.isAuthenticated) {
          const event = new CustomEvent("show-toast", {
            detail: { 
              title: "Authentication Required", 
              message: "Please login to continue.",
              type: "warning"
            }
          });
          window.dispatchEvent(event);
          this.$router.push('/ipa/sign-in');
          return;
        }

        const token = localStorage.getItem("access_token");
        
        // Get user details from auth store
        const user = this.authStore.user;
        const customerName = user?.full_name || user?.name || user?.customer_name || 'Customer';
        
        // Note: customer_phone will be assigned by backend from customer account
        // We still include it in payload for compatibility, but backend will override with account phone
        
        // Prepare order payload with discount information
        const orderPayload = {
          customer_name: customerName.trim(),
          customer_phone: user?.phone || user?.phone_number || user?.customer_phone || '', // Backend will use account phone
          delivery_address: this.delivery.address.trim(),
          delivery_city: this.delivery.city.trim(),
          pin_location: this.delivery.pinLocation && this.delivery.pinLocation.trim() ? this.delivery.pinLocation.trim() : null,
          delivery_notes: this.delivery.notes && this.delivery.notes.trim() ? this.delivery.notes.trim() : null,
          delivery_fee: parseFloat((this.delivery.isWithinCBD ? 0 : this.deliveryFee).toFixed(2)),
          items: this.cartItems.map(item => {
            const finalPrice = this.cartStore.getItemPrice(item);
            const pct = this.itemDiscountPercent(item);
            ensureItemSizeSlots(item);
            const payloadItem = {
              item_id: String(item.item_id),
              name: String(item.name).trim(),
              quantity: parseInt(item.quantity, 10),
              price: parseFloat(Number(item.price).toFixed(2)),
              final_price: finalPrice !== undefined && finalPrice !== null ? parseFloat(Number(finalPrice).toFixed(2)) : null,
              discount_percentage:
                pct != null ? parseFloat(Number(pct).toFixed(2)) : null,
              discounted: Boolean(item.discounted),
            };
            if (item.sub_category) {
              payloadItem.sub_category = String(item.sub_category).trim().toLowerCase();
            }
            if (item.color_id) {
              payloadItem.color_id = String(item.color_id);
              if (item.color_label) {
                payloadItem.color_label = String(item.color_label);
              }
            }
            if (itemNeedsSize(item) && Array.isArray(item.sizes) && item.sizes.length) {
              payloadItem.sizes = item.sizes.map((value) => String(value).trim());
            }
            return payloadItem;
          }),
          subtotal: parseFloat(Number(this.subtotal).toFixed(2)),
          discount_savings: parseFloat(Number(this.discountSavings || 0).toFixed(2)),
          total_amount: parseFloat(Number(this.total).toFixed(2)),
          payment_method: "cash_on_delivery",
          currency: "KES",
          status: "pending"
        };
        
        // Validate payload before sending
        const validation = this.validateOrderPayload(orderPayload);
        if (!validation.isValid) {
          const errorMessage = validation.errors.slice(0, 3).join('. '); // Show first 3 errors
          const event = new CustomEvent("show-toast", {
            detail: { 
              title: "Validation Error", 
              message: errorMessage + (validation.errors.length > 3 ? ` (and ${validation.errors.length - 3} more)` : ''),
              type: "error"
            }
          });
          window.dispatchEvent(event);
          console.error('Order payload validation errors:', validation.errors);
          this.isProcessing = false;
          return;
        }
        
        const hmacPayload = generateHmacSignature(orderPayload);

        const response = await fetch(buildApiUrl("/orders/order-creation"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": token ? `Bearer ${token}` : ''
          },
          body: JSON.stringify(hmacPayload)
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.detail || "Order processing failed. Please try again.");
        }

        // Order successful
        this.orderStatus = 'success';
        
        // Clear cart
        try {
          this.cartStore.clearCart();
        } catch (error) {
          console.error("Error clearing cart after order:", error);
          // Cart clearing failed but order was successful, so continue
        }
        
        // Store order confirmation data
        this.orderConfirmation = {
          orderNumber: result.order_number,
          orderId: result.order_id,
          subtotal: result.subtotal,
          discountSavings: result.discount_savings,
          totalAmount: result.total_amount,
          currency: result.currency
        };
        
        // Show success message with order details

        setTimeout(() => {
          if (this.orderStatus === 'success') {
            this.closeModalAndRedirect();
          }
        }, 5000);

      } catch (err) {
        console.error("Order Error:", err);
        this.orderStatus = 'error';
        this.errorMessage = err.message || "An unexpected error occurred. Please try again.";
      } finally {
        this.isProcessing = false;
      }
    },

    retryOrder() {
      this.orderStatus = null;
      this.errorMessage = "";
    },

    closeModal() {
      if (!this.isProcessing) {
        this.showModal = false;
        this.orderStatus = null;
        this.errorMessage = "";
      }
    },

    closeModalAndRedirect() {
      this.showModal = false;
      this.orderStatus = null;
      // Redirect to order confirmation page or store
      this.$router.push("/ipa/store");
    },

    increaseQty(item) {
      try {
        this.cartStore.increaseQty(item);
        // Use global toast system
        const event = new CustomEvent("show-toast", {
          detail: { 
            title: "Cart Updated", 
            message: `Increased quantity of ${item.name}`,
            type: "success"
          }
        });
        window.dispatchEvent(event);
      } catch (error) {
        console.error("Error increasing quantity:", error);
        const errorEvent = new CustomEvent("show-toast", {
          detail: { 
            title: "Error", 
            message: error.message || "Failed to update cart. Please log in again.",
            type: "error"
          }
        });
        window.dispatchEvent(errorEvent);
      }
    },

    decreaseQty(item) {
      try {
        this.cartStore.decreaseQty(item);
        // Use global toast system
        const event = new CustomEvent("show-toast", {
          detail: { 
            title: "Cart Updated", 
            message: `Decreased quantity of ${item.name}`,
            type: "success"
          }
        });
        window.dispatchEvent(event);
      } catch (error) {
        console.error("Error decreasing quantity:", error);
        const errorEvent = new CustomEvent("show-toast", {
          detail: { 
            title: "Error", 
            message: error.message || "Failed to update cart. Please log in again.",
            type: "error"
          }
        });
        window.dispatchEvent(errorEvent);
      }
    },

    removeItem(item) {
      // Use the global toast system instead of browser confirm
      const event = new CustomEvent("show-toast", {
        detail: { 
          title: "Remove Item?", 
          message: `Are you sure you want to remove ${item.name} from your cart?`,
          type: "warning"
        }
      });
      window.dispatchEvent(event);
      
      // Simple confirmation - in production, consider a proper modal
      setTimeout(() => {
        if (confirm(`Remove ${item.name} from cart?`)) {
          try {
            this.cartStore.removeFromCart(item);
            const successEvent = new CustomEvent("show-toast", {
              detail: { 
                title: "Item Removed", 
                message: `${item.name} has been removed from your cart.`,
                type: "success"
              }
            });
            window.dispatchEvent(successEvent);
          } catch (error) {
            console.error("Error removing item:", error);
            const errorEvent = new CustomEvent("show-toast", {
              detail: { 
                title: "Error", 
                message: error.message || "Failed to remove item. Please log in again.",
                type: "error"
              }
            });
            window.dispatchEvent(errorEvent);
          }
        }
      }, 100);
    },

    getUserName() {
      if (!this.authStore.user) return 'N/A';
      const user = this.authStore.user;
      return user.full_name || user.name || user.customer_name || 'Customer';
    },

    getUserPhone() {
      if (!this.authStore.user) return 'N/A';
      const user = this.authStore.user;
      return user.phone || user.phone_number || user.customer_phone || 'N/A';
    }
  },
};
</script>

<style scoped>
/* ================================
   GLOBAL RESET & BASE
   ================================ */
.cart-page {
  min-height: 100vh;
  background-color: var(--background-color1);
  color: var(--text-color1);
  font-family: "Poppins", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  overflow-x: hidden;
}

/* ================================
   BLUR EFFECT
   ================================ */
.blurred {
  filter: blur(4px);
  transition: filter 0.25s ease;
  pointer-events: none;
  user-select: none;
}

/* ================================
   TOAST NOTIFICATION STYLES
   ================================ */
.toast-notification {
  position: fixed;
  top: 20px;
  right: 20px;
  left: 20px;
  max-width: 400px;
  margin: 0 auto;
  background: var(--background-color2);
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  border-left: 4px solid #3498db;
  z-index: 10001;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  animation: slideIn 0.3s ease-out;
  transform-origin: top center;
}

.toast-notification.info {
  border-left-color: #3498db;
  background: linear-gradient(135deg, var(--background-color2) 0%, rgba(52, 152, 219, 0.05) 100%);
}

.toast-notification.success {
  border-left-color: #2ecc71;
  background: linear-gradient(135deg, var(--background-color2) 0%, rgba(46, 204, 113, 0.05) 100%);
}

.toast-notification.warning {
  border-left-color: #f39c12;
  background: linear-gradient(135deg, var(--background-color2) 0%, rgba(243, 156, 18, 0.05) 100%);
}

.toast-notification.error {
  border-left-color: #e74c3c;
  background: linear-gradient(135deg, var(--background-color2) 0%, rgba(231, 76, 60, 0.05) 100%);
}

.toast-content {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex: 1;
}

.toast-content i {
  font-size: 1.2rem;
  margin-top: 2px;
  flex-shrink: 0;
}

.toast-notification.info .toast-content i {
  color: #3498db;
}

.toast-notification.success .toast-content i {
  color: #2ecc71;
}

.toast-notification.warning .toast-content i {
  color: #f39c12;
}

.toast-notification.error .toast-content i {
  color: #e74c3c;
}

.toast-message {
  flex: 1;
}

.toast-message strong {
  display: block;
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 4px;
  color: var(--text-color1);
}

.toast-message p {
  margin: 0;
  font-size: 0.85rem;
  color: var(--text-color2);
  line-height: 1.4;
}

.toast-close {
  background: none;
  border: none;
  color: var(--text-color2);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
  flex-shrink: 0;
  margin-left: 8px;
}

.toast-close:hover {
  background: rgba(0, 0, 0, 0.05);
  color: var(--text-color1);
}

/* Toast Animations */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-20px) scale(0.95);
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes slideOut {
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
}

/* Desktop toast positioning */
@media (min-width: 768px) {
  .toast-notification {
    left: auto;
    right: 20px;
    max-width: 380px;
  }
}

/* ================================
   CART SECTION LAYOUT
   ================================ */
.cart-section {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: clamp(70px, 8vw, 90px) clamp(12px, 3vw, 24px) 40px;
  box-sizing: border-box;
}

.cart-header {
  margin-bottom: clamp(24px, 4vw, 36px);
}

.cart-header-top {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: start;
  gap: clamp(12px, 2vw, 16px);
}

.cart-header-text {
  grid-column: 2;
  text-align: center;
  justify-self: center;
}

.my-orders-btn {
  grid-column: 3;
  justify-self: end;
  align-self: center;
}

.cart-heading {
  font-size: clamp(1.8rem, 5vw, 2.5rem);
  margin-bottom: clamp(8px, 1.5vw, 12px);
  color: var(--border-color-hover);
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: clamp(10px, 1.5vw, 14px);
}

.cart-heading i {
  font-size: clamp(1.5rem, 4vw, 2rem);
}

.cart-subtitle {
  font-size: clamp(0.9rem, 1.6vw, 1.1rem);
  color: var(--text-color2);
  margin: 0;
}

/* ================================
   CART LIST - HIGHLY RESPONSIVE
   ================================ */
.cart-container {
  display: flex;
  flex-direction: column;
  gap: clamp(12px, 2vw, 18px);
  margin-bottom: clamp(20px, 4vw, 30px);
}

.cart-item {
  display: flex;
  align-items: flex-start;
  background: var(--background-color2);
  padding: clamp(16px, 2.5vw, 20px);
  border-radius: clamp(12px, 2vw, 16px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0,0,0,0.05);
  gap: clamp(16px, 2.5vw, 20px);
  animation: fadeIn 0.4s ease forwards;
  opacity: 0;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.cart-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: var(--border-color-hover);
  transform: scaleY(0);
  transition: transform 0.3s ease;
}

.cart-item:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  border-color: rgba(var(--border-color-hover-rgb), 0.2);
  transform: translateY(-2px);
}

.cart-item:hover::before {
  transform: scaleY(1);
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.product-img-wrapper {
  position: relative;
  flex-shrink: 0;
}

.product-img {
  width: clamp(80px, 14vw, 120px);
  height: clamp(80px, 14vw, 120px);
  min-width: clamp(80px, 14vw, 120px);
  border-radius: clamp(10px, 1.5vw, 12px);
  object-fit: cover;
  background: linear-gradient(180deg, rgba(0,0,0,0.02), rgba(0,0,0,0.01));
  transition: transform 0.3s ease;
  border: 1px solid rgba(0,0,0,0.05);
}

.cart-item:hover .product-img {
  transform: scale(1.05);
}

.img-discount-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  background: linear-gradient(135deg, #ff4444, #ff6b6b);
  color: white;
  padding: clamp(3px, 0.5vw, 4px) clamp(6px, 1vw, 8px);
  border-radius: 4px;
  font-size: clamp(0.65rem, 1.2vw, 0.7rem);
  font-weight: 700;
  box-shadow: 0 2px 6px rgba(255, 68, 68, 0.3);
  z-index: 1;
}

.product-info {
  flex: 1;
  min-width: 0;
  text-align: left;
}

.product-name {
  font-size: clamp(1rem, 2vw, 1.15rem);
  margin: 0 0 clamp(6px, 1.2vw, 8px) 0;
  font-weight: 600;
  color: var(--text-color1);
  line-height: 1.4;
  transition: color 0.2s ease;
}

.cart-item:hover .product-name {
  color: var(--border-color-hover);
}

.product-color-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 clamp(6px, 1vw, 8px) 0;
  font-size: clamp(0.72rem, 1.3vw, 0.8rem);
  color: var(--text-color2);
}

.product-color-swatch {
  display: inline-flex;
  width: 16px;
  height: 16px;
  border-radius: 3px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
}

.product-color-swatch__half {
  display: block;
  width: 50%;
  height: 100%;
}

.description {
  font-size: clamp(0.7rem, 1.4vw, 0.8rem);
  color: var(--text-color2);
  margin: 0 0 clamp(8px, 1.5vw, 12px) 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ================================
   PRICE DISPLAY WITH DISCOUNTS
   ================================ */
.price-display {
  margin-bottom: 12px;
}

.discounted-price {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.original-price.crossed {
  font-size: clamp(0.75rem, 1.4vw, 0.85rem);
  color: #888;
  text-decoration: line-through;
}

.final-price.highlight {
  font-size: clamp(0.9rem, 1.8vw, 1rem);
  font-weight: 700;
  color: #ff4444;
}

.price-row-main {
  display: flex;
  align-items: center;
  gap: clamp(8px, 1.5vw, 12px);
  flex-wrap: wrap;
}

.discount-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: linear-gradient(135deg, #ff4444, #ff6b6b);
  color: white;
  padding: clamp(4px, 0.8vw, 6px) clamp(8px, 1.2vw, 10px);
  border-radius: 6px;
  font-size: clamp(0.7rem, 1.3vw, 0.75rem);
  font-weight: 700;
  align-self: flex-start;
  margin-top: clamp(4px, 0.8vw, 6px);
  box-shadow: 0 2px 6px rgba(255, 68, 68, 0.2);
}

.discount-badge i {
  font-size: clamp(0.65rem, 1.2vw, 0.7rem);
}

.regular-price {
  margin-top: 8px;
}

.normal-price {
  font-size: clamp(0.9rem, 1.8vw, 1rem);
  font-weight: 700;
  color: var(--border-color-hover);
}

/* ================================
   QUANTITY CONTROLS - ENHANCED
   ================================ */
.quantity-wrapper {
  margin-top: clamp(12px, 2vw, 16px);
}

.size-picker-block {
  margin-top: clamp(10px, 1.6vw, 14px);
  padding: clamp(10px, 1.6vw, 12px);
  border-radius: 10px;
  background: var(--background-color3, #f5f7fa);
  border: 1px solid var(--border-color, rgba(0, 0, 0, 0.08));
}

.size-picker-block__title {
  margin: 0 0 0.65rem;
  font-size: clamp(0.75rem, 1.3vw, 0.85rem);
  font-weight: 600;
  color: var(--text-color1);
  display: flex;
  align-items: center;
  gap: 0.45rem;
}

.size-picker-row {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 0.55rem;
}

.size-picker-row:last-child {
  margin-bottom: 0;
}

.size-picker-row__label {
  min-width: 4.5rem;
  font-size: clamp(0.72rem, 1.2vw, 0.8rem);
  color: var(--text-color2);
}

.size-picker-row__select {
  flex: 1;
  min-width: 0;
  padding: 0.45rem 0.55rem;
  border-radius: 8px;
  border: 1px solid var(--border-color, rgba(0, 0, 0, 0.12));
  background: var(--background-color1, #fff);
  color: var(--text-color1);
  font-size: clamp(0.8rem, 1.4vw, 0.9rem);
}

.item-size-summary {
  display: block;
  margin-top: 0.2rem;
  font-size: 0.78rem;
  color: var(--text-color2);
}

.quantity-label {
  display: block;
  font-size: clamp(0.75rem, 1.3vw, 0.85rem);
  color: var(--text-color2);
  margin-bottom: clamp(6px, 1vw, 8px);
  font-weight: 500;
}

.quantity {
  display: inline-flex;
  align-items: center;
  gap: clamp(8px, 1.2vw, 10px);
  background: var(--background-color1);
  border-radius: clamp(8px, 1.2vw, 10px);
  padding: clamp(4px, 0.8vw, 6px);
  border: 1px solid rgba(0,0,0,0.08);
}

.qty-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--background-color2);
  color: var(--border-color-hover);
  border: 1px solid rgba(var(--border-color-hover-rgb), 0.2);
  width: clamp(32px, 5vw, 36px);
  height: clamp(32px, 5vw, 36px);
  border-radius: clamp(6px, 1vw, 8px);
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  font-size: clamp(0.85rem, 1.4vw, 0.95rem);
}

.qty-btn:hover:not(:disabled) {
  background: var(--border-color-hover);
  color: var(--background-color2);
  transform: scale(1.1);
  box-shadow: 0 2px 8px rgba(var(--border-color-hover-rgb), 0.3);
}

.qty-btn:active:not(:disabled) {
  transform: scale(0.95);
}

.qty-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none !important;
}

.qty-value {
  font-size: clamp(1rem, 1.8vw, 1.1rem);
  font-weight: 700;
  color: var(--text-color1);
  min-width: clamp(32px, 5vw, 40px);
  text-align: center;
  padding: 0 clamp(4px, 0.8vw, 6px);
}

/* ================================
   ITEM TOTAL SECTION
   ================================ */
.item-total-section {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  min-width: clamp(100px, 15vw, 140px);
}

.total-price {
  text-align: right;
}

.total-label {
  font-size: clamp(0.75rem, 1.3vw, 0.85rem);
  color: var(--text-color2);
  margin: 0 0 clamp(4px, 0.8vw, 6px) 0;
  font-weight: 500;
}

.total {
  font-size: clamp(1.1rem, 2vw, 1.3rem);
  color: var(--border-color-hover);
  font-weight: 700;
  margin: 0;
  white-space: nowrap;
}

.savings {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: clamp(0.75rem, 1.3vw, 0.8rem);
  color: #28a745;
  font-weight: 600;
  margin-top: clamp(6px, 1vw, 8px);
  padding: clamp(3px, 0.5vw, 4px) clamp(6px, 1vw, 8px);
  background: rgba(40, 167, 69, 0.1);
  border-radius: 4px;
}

.savings i {
  font-size: clamp(0.7rem, 1.2vw, 0.75rem);
}

.remove-item {
  background: transparent;
  border: 1px solid rgba(231, 76, 60, 0.2);
  color: var(--text-color2);
  padding: clamp(8px, 1.2vw, 10px) clamp(12px, 1.8vw, 16px);
  border-radius: clamp(6px, 1vw, 8px);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: clamp(6px, 1vw, 8px);
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: clamp(0.8rem, 1.4vw, 0.9rem);
  font-weight: 500;
  margin-top: clamp(8px, 1.2vw, 12px);
}

.remove-item:hover {
  background: rgba(231, 76, 60, 0.1);
  color: #e74c3c;
  border-color: rgba(231, 76, 60, 0.4);
  transform: translateY(-1px);
}

.remove-text {
  font-size: clamp(0.75rem, 1.3vw, 0.85rem);
}

@media (max-width: 640px) {
  .remove-item {
    padding: clamp(6px, 1vw, 8px) clamp(10px, 1.5vw, 12px);
  }
  
  .remove-text {
    display: none;
  }
}

/* ================================
   ORDER SECTION - RESPONSIVE GRID
   ================================ */
.order-section {
  display: grid;
  grid-template-columns: 1fr;
  gap: clamp(20px, 4vw, 30px);
  margin-bottom: clamp(40px, 6vw, 60px);
}

@media (min-width: 768px) {
  .order-section {
    grid-template-columns: 1fr 1fr;
  }
}

@media (min-width: 1024px) {
  .order-section {
    grid-template-columns: 1fr clamp(400px, 35vw, 450px);
  }
}

.order-summary,
.delivery-form {
  background: var(--background-color2);
  border-radius: clamp(10px, 1.5vw, 12px);
  padding: clamp(16px, 3vw, 24px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid rgba(0,0,0,0.03);
}

.order-summary h2,
.delivery-form h2 {
  margin: 0 0 clamp(16px, 2.5vw, 20px) 0;
  font-size: clamp(1.1rem, 2vw, 1.3rem);
  font-weight: 700;
  color: var(--text-color1);
  display: flex;
  align-items: center;
  gap: clamp(8px, 1.2vw, 10px);
  padding-bottom: clamp(12px, 2vw, 16px);
  border-bottom: 2px solid rgba(var(--border-color-hover-rgb), 0.2);
}

.order-summary h2::before,
.delivery-form h2::before {
  content: '';
  width: 4px;
  height: 24px;
  background: var(--border-color-hover);
  border-radius: 2px;
}

/* ================================
   ORDER SUMMARY
   ================================ */
.summary-item,
.summary-total {
  display: flex;
  justify-content: space-between;
  margin: clamp(10px, 1.8vw, 14px) 0;
  font-size: clamp(0.85rem, 1.6vw, 0.95rem);
  align-items: center;
  padding: clamp(6px, 1vw, 8px) 0;
}

.summary-item span:first-child {
  display: flex;
  align-items: center;
  gap: clamp(6px, 1vw, 8px);
  color: var(--text-color1);
}

.summary-item span:first-child i {
  color: var(--border-color-hover);
  font-size: clamp(0.8rem, 1.4vw, 0.9rem);
}

.summary-item.savings {
  color: #28a745;
  background: rgba(40, 167, 69, 0.05);
  padding: clamp(6px, 1vw, 8px) clamp(8px, 1.5vw, 12px);
  border-radius: 6px;
}

.summary-item.savings .savings-amount {
  color: #28a745;
  font-weight: 600;
}

.summary-item.final-subtotal {
  background: rgba(76, 175, 80, 0.1);
  padding: clamp(8px, 1.2vw, 10px);
  border-radius: 6px;
  margin: 12px 0;
  font-weight: 600;
}

.summary-total {
  margin-top: clamp(16px, 2.5vw, 20px);
  font-size: clamp(1.1rem, 2vw, 1.3rem);
  padding: clamp(18px, 2.8vw, 24px);
  border-top: 2px solid var(--border-color-hover);
  background: linear-gradient(135deg, rgba(var(--border-color-hover-rgb), 0.1), rgba(var(--border-color-hover-rgb), 0.05));
  border-radius: clamp(10px, 1.5vw, 12px);
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 8px rgba(var(--border-color-hover-rgb), 0.1);
}

.total-left {
  display: flex;
  flex-direction: column;
  gap: clamp(2px, 0.5vw, 4px);
}

.total-note {
  font-size: clamp(0.7rem, 1.2vw, 0.75rem);
  color: var(--text-color2);
  font-weight: 400;
}

.total-amount {
  font-size: clamp(1.3rem, 2.5vw, 1.6rem);
  color: var(--border-color-hover);
}

hr,
.summary-divider { 
  border: none; 
  border-top: 2px solid rgba(var(--border-color-hover-rgb), 0.15); 
  margin: clamp(16px, 2.5vw, 20px) 0; 
}

.delivery-badge {
  display: inline-flex;
  align-items: center;
  gap: clamp(4px, 0.8vw, 6px);
  border-radius: 20px;
  padding: clamp(4px, 0.8vw, 6px) clamp(10px, 1.5vw, 12px);
  font-size: clamp(0.75rem, 1.3vw, 0.8rem);
  font-weight: 600;
}

.delivery-badge.free {
  background: linear-gradient(135deg, #4CAF50, #66BB6A);
  color: white;
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
}

.delivery-badge.charged {
  background: rgba(243, 156, 18, 0.1);
  color: #f39c12;
  border: 1px solid rgba(243, 156, 18, 0.3);
}

.delivery-badge i {
  font-size: clamp(0.7rem, 1.2vw, 0.75rem);
}

.payment-note {
  margin-top: clamp(16px, 2.5vw, 20px);
  font-size: clamp(0.8rem, 1.5vw, 0.9rem);
  color: var(--text-color1);
  display: flex;
  align-items: flex-start;
  gap: clamp(10px, 1.5vw, 12px);
  padding: clamp(12px, 2vw, 16px);
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.1), rgba(76, 175, 80, 0.05));
  border-radius: clamp(8px, 1.2vw, 10px);
  border-left: 3px solid #4CAF50;
}

.payment-note i {
  color: #4CAF50;
  font-size: clamp(1rem, 1.8vw, 1.2rem);
  margin-top: 2px;
  flex-shrink: 0;
}

.payment-note-content {
  display: flex;
  flex-direction: column;
  gap: clamp(2px, 0.5vw, 4px);
}

.payment-note-content strong {
  color: var(--text-color1);
  font-size: clamp(0.85rem, 1.5vw, 0.95rem);
}

.payment-note-content span {
  color: var(--text-color2);
  font-size: clamp(0.75rem, 1.3vw, 0.85rem);
}

/* ================================
   FORM STYLES - RESPONSIVE
   ================================ */
.form-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: clamp(12px, 2vw, 16px);
}

@media (min-width: 480px) {
  .form-grid {
    grid-template-columns: 1fr 1fr;
  }
}

.form-group {
  margin-bottom: clamp(12px, 2vw, 16px);
}

.form-group label {
  display: flex;
  align-items: center;
  gap: clamp(6px, 1vw, 8px);
  margin-bottom: clamp(6px, 1vw, 8px);
  font-size: clamp(0.85rem, 1.5vw, 0.95rem);
  font-weight: 600;
  color: var(--text-color1);
}

.form-group label i {
  color: var(--border-color-hover);
  font-size: clamp(0.8rem, 1.4vw, 0.9rem);
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: clamp(10px, 1.5vw, 12px);
  border-radius: clamp(6px, 1vw, 8px);
  border: 1px solid rgba(0,0,0,0.1);
  background: var(--background-color1);
  color: var(--text-color1);
  box-sizing: border-box;
  font-family: "Poppins", sans-serif;
  font-size: clamp(0.85rem, 1.4vw, 0.9rem);
  transition: all 0.2s ease;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--border-color-hover);
  box-shadow: 0 0 0 3px rgba(var(--border-color-hover-rgb), 0.1);
  transform: translateY(-1px);
}

.form-group input.input-error,
.form-group textarea.input-error {
  border-color: #e74c3c;
  box-shadow: 0 0 0 3px rgba(231, 76, 60, 0.1);
}

.form-group input.input-error:focus,
.form-group textarea.input-error:focus {
  border-color: #e74c3c;
  box-shadow: 0 0 0 3px rgba(231, 76, 60, 0.2);
}

.city-hint,
.field-hint {
  display: flex;
  align-items: center;
  gap: clamp(4px, 0.8vw, 6px);
  margin-top: clamp(6px, 1vw, 8px);
  font-size: clamp(0.75rem, 1.3vw, 0.8rem);
  color: #666;
}

.city-hint.fee-applies {
  color: #e74c3c;
  font-weight: 500;
}

.city-hint.free-delivery {
  color: #28a745;
  font-weight: 500;
}

.city-hint i,
.field-hint i {
  font-size: clamp(0.7rem, 1.2vw, 0.75rem);
}

.error-message {
  display: flex;
  align-items: center;
  gap: clamp(4px, 0.8vw, 6px);
  margin-top: clamp(4px, 0.8vw, 6px);
  font-size: clamp(0.7rem, 1.2vw, 0.75rem);
  color: #e74c3c;
  font-weight: 500;
}

.error-message i {
  font-size: clamp(0.65rem, 1.1vw, 0.7rem);
}

.char-count {
  color: var(--text-color2);
  font-size: clamp(0.7rem, 1.2vw, 0.75rem);
}

.pin-link {
  color: var(--border-color-hover);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: opacity 0.2s ease;
}

.pin-link:hover {
  opacity: 0.8;
  text-decoration: underline;
}

.location-help-label {
  display: flex;
  align-items: center;
  gap: clamp(6px, 1vw, 8px);
  margin-bottom: clamp(6px, 1vw, 8px);
  font-size: clamp(0.85rem, 1.5vw, 0.95rem);
  font-weight: 600;
  color: var(--text-color1);
}

.location-help-label i {
  color: var(--border-color-hover);
  font-size: clamp(0.8rem, 1.4vw, 0.9rem);
}

.location-help-intro {
  margin-bottom: clamp(10px, 1.6vw, 12px);
  align-items: flex-start;
  line-height: 1.4;
}

.location-mode-toggle {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(6px, 1vw, 8px);
  margin-bottom: clamp(10px, 1.6vw, 12px);
}

.location-mode-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 42px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid rgba(var(--border-color-hover-rgb), 0.28);
  background: var(--background-color1);
  color: var(--text-color1);
  font-family: inherit;
  font-size: clamp(0.78rem, 1.4vw, 0.88rem);
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.location-mode-btn i {
  font-size: 0.85rem;
}

.location-mode-btn:hover {
  border-color: var(--border-color-hover);
}

.location-mode-btn.active {
  background: var(--border-color-hover);
  border-color: var(--border-color-hover);
  color: var(--text-color3, #fff);
}

.location-mode-panel .field-hint {
  align-items: flex-start;
  line-height: 1.4;
}

.live-location-copy {
  margin: 0 0 clamp(8px, 1.2vw, 10px);
  font-size: clamp(0.75rem, 1.3vw, 0.82rem);
  line-height: 1.45;
  color: var(--text-color2);
}

.live-location-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 44px;
  margin-top: clamp(8px, 1.2vw, 10px);
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid var(--border-color-hover);
  background: var(--background-color1);
  color: var(--border-color-hover);
  font-family: inherit;
  font-size: clamp(0.82rem, 1.4vw, 0.9rem);
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;
}

.live-location-btn:hover:not(:disabled) {
  background: rgba(var(--border-color-hover-rgb), 0.08);
  transform: translateY(-1px);
}

.live-location-btn:disabled {
  opacity: 0.65;
  cursor: wait;
}

.live-location-captured {
  margin-top: clamp(6px, 1vw, 8px);
  padding: clamp(10px, 1.5vw, 12px);
  border-radius: 8px;
  background: rgba(40, 167, 69, 0.08);
  border: 1px solid rgba(40, 167, 69, 0.22);
}

.live-location-captured__status {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.live-location-captured__status i {
  color: #28a745;
  margin-top: 2px;
}

.live-location-captured__status div {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: clamp(0.75rem, 1.3vw, 0.82rem);
  color: var(--text-color2);
}

.live-location-captured__status strong {
  color: var(--text-color1);
  font-size: clamp(0.8rem, 1.4vw, 0.88rem);
}

.live-location-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
}

.location-text-btn {
  background: none;
  border: none;
  padding: 0;
  color: var(--text-color2);
  font-family: inherit;
  font-size: clamp(0.75rem, 1.3vw, 0.82rem);
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
}

.location-text-btn:hover {
  color: #e74c3c;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.checkbox-group {
  margin-top: clamp(16px, 2.5vw, 20px);
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: clamp(6px, 1vw, 8px);
  cursor: pointer;
  font-size: clamp(0.8rem, 1.4vw, 0.9rem);
  line-height: 1.4;
}

.checkbox-label input[type="checkbox"] {
  display: none;
}

.checkmark {
  width: clamp(16px, 2.5vw, 18px);
  height: clamp(16px, 2.5vw, 18px);
  border: 2px solid rgba(0,0,0,0.2);
  border-radius: 4px;
  position: relative;
  transition: all 0.2s;
  flex-shrink: 0;
}

.checkbox-label input[type="checkbox"]:checked + .checkmark {
  background: var(--border-color-hover);
  border-color: var(--border-color-hover);
}

.checkbox-label input[type="checkbox"]:checked + .checkmark::after {
  content: "✓";
  position: absolute;
  color: white;
  font-size: clamp(10px, 1.5vw, 12px);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

/* ================================
   CHECKOUT BUTTON — MatchesHub "Create team"
   ================================ */
.link-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 12px clamp(18px, 4vw, 24px);
  font-size: clamp(0.85rem, 2vw, 0.92rem);
  font-weight: 600;
  font-family: inherit;
  border-radius: 32px;
  border: 1px solid var(--border-color-hover1);
  background: var(--background-color2);
  color: var(--text-color1);
  cursor: pointer;
  text-decoration: none;
  transition: transform 0.2s, border-color 0.2s, box-shadow 0.2s;
}

.link-btn:hover:not(:disabled) {
  border-color: var(--border-color-hover);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px var(--box-shadow);
}

.link-btn--primary {
  background: var(--border-color-hover);
  border-color: var(--border-color-hover);
  color: var(--text-color3);
  box-shadow: 0 4px 15px var(--box-shadow);
}

.link-btn--primary:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: 0 6px 18px var(--box-shadow);
}

/* ================================
   CHECKOUT BUTTON — Pathfinder plan CTA style
   ================================ */
.checkout-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: clamp(16px, 2.5vw, 24px);
  padding: 14px 20px;
  border-radius: 32px;
  font-size: 1rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  background: var(--border-color-hover);
  color: var(--text-color3);
  border: 1px solid transparent;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.checkout-btn:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.15);
}

.checkout-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* ================================
   EMPTY STATE - ENHANCED
   ================================ */
.empty-cart {
  padding: clamp(40px, 8vw, 80px) clamp(20px, 4vw, 40px);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
}

.empty-cart-content {
  text-align: center;
  max-width: 500px;
  animation: fadeInUp 0.5s ease-out;
}

.empty-cart-icon-wrapper {
  position: relative;
  display: inline-block;
  margin-bottom: clamp(20px, 4vw, 30px);
}

.empty-icon { 
  font-size: clamp(64px, 12vw, 96px); 
  color: var(--border-color-hover);
  opacity: 0.7;
  animation: float 3s ease-in-out infinite;
}

.empty-cart-pulse {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: clamp(80px, 15vw, 120px);
  height: clamp(80px, 15vw, 120px);
  border: 2px solid var(--border-color-hover);
  border-radius: 50%;
  opacity: 0.3;
  animation: pulse 2s ease-in-out infinite;
}

.empty-cart-title {
  font-size: clamp(1.5rem, 4vw, 2rem);
  font-weight: 700;
  color: var(--text-color1);
  margin: 0 0 clamp(12px, 2vw, 16px) 0;
}

.empty-cart-message {
  font-size: clamp(0.9rem, 1.6vw, 1.1rem);
  color: var(--text-color2);
  margin-bottom: clamp(24px, 4vw, 32px);
  line-height: 1.6;
}

/* Match MatchesHub.vue "Create a team" (.btn.btn-primary.btn-text-color3) */
.empty-cart .browse-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: clamp(8px, 1.5vw, 12px);
  padding: 14px 26px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  cursor: pointer;
  border: 1px solid var(--border-color-hover);
  background: linear-gradient(135deg, var(--border-color-hover), #40e0e0);
  color: var(--text-color3);
  box-shadow: 0 8px 28px rgba(0, 255, 255, 0.35);
  transition:
    transform 0.28s ease,
    box-shadow 0.28s ease,
    background 0.28s ease,
    border-color 0.28s ease;
}

.empty-cart .browse-btn i {
  font-size: 0.95rem;
  color: var(--text-color3);
}

.empty-cart .browse-btn:hover {
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 14px 36px rgba(0, 255, 255, 0.45);
  border-color: var(--border-color-hover);
}

@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
}

@keyframes pulse {
  0%, 100% { 
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.3;
  }
  50% { 
    transform: translate(-50%, -50%) scale(1.2);
    opacity: 0.1;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ================================
   MODAL STYLES (Compact & Scrollable)
   ================================ */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 10000;
  animation: fadeIn 0.2s ease-out;
}

.confirm-modal {
  width: 100%;
  max-width: 500px;
  max-height: 85vh;
  background: var(--background-color2);
  color: var(--text-color1);
  font-family: "Inter", system-ui, sans-serif;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: modalSlideUp 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border: 1px solid rgba(var(--border-color-hover-rgb), 0.2);
}

/* Scrollable content area */
.scrollable-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  max-height: calc(85vh - 180px);
}

.scrollable-content::-webkit-scrollbar {
  width: 6px;
}

.scrollable-content::-webkit-scrollbar-track {
  background: var(--background-color1);
  border-radius: 3px;
}

.scrollable-content::-webkit-scrollbar-thumb {
  background: var(--border-color-hover);
  border-radius: 3px;
}

.scrollable-content::-webkit-scrollbar-thumb:hover {
  background: rgba(var(--border-color-hover-rgb), 0.8);
}

/* Header */
.confirm-header {
  text-align: center;
  padding: 20px 20px 15px;
  border-bottom: 1px solid rgba(var(--border-color-hover-rgb), 0.1);
  background: var(--background-color1);
}

.confirm-header h2 {
  font-size: 1.4rem;
  font-weight: 600;
  margin: 0 0 5px 0;
  color: var(--text-color1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.confirm-header .subtitle {
  font-size: 0.85rem;
  color: #94a3b8;
  margin: 0;
}

/* Sections */
.confirm-section {
  background: var(--background-color1);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  border: 1px solid rgba(var(--border-color-hover-rgb), 0.1);
}

.confirm-section h3 {
  font-size: 0.95rem;
  font-weight: 600;
  margin: 0 0 12px 0;
  color: var(--text-color1);
  display: flex;
  align-items: center;
  gap: 8px;
}

.confirm-section h3 i {
  color: var(--border-color-hover);
  font-size: 0.9rem;
}

/* Items List */
.items-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.item-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(var(--border-color-hover-rgb), 0.1);
}

.item-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.item-info {
  flex: 1;
  min-width: 0;
}

.item-name {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-color1);
  display: block;
  margin-bottom: 4px;
  line-height: 1.3;
}

.item-color-meta {
  display: block;
  font-size: 0.75rem;
  color: var(--text-color2);
  margin-bottom: 2px;
}

.item-quantity {
  font-size: 0.8rem;
  color: #94a3b8;
  font-weight: 600;
}

.item-pricing {
  text-align: right;
  min-width: 120px;
}

.discounted-price-row {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  margin-bottom: 4px;
}

.price-details {
  display: flex;
  align-items: center;
  gap: 6px;
}

.original-price-small {
  font-size: 0.75rem;
  color: #888;
  text-decoration: line-through;
}

.final-price-small {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--border-color-hover);
}

.item-savings-badge {
  font-size: 0.7rem;
  color: #28a745;
  font-weight: 600;
  background: rgba(40, 167, 69, 0.1);
  padding: 2px 8px;
  border-radius: 4px;
  display: inline-block;
}

.regular-price-row .item-price {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--border-color-hover);
}

.item-total {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text-color1);
  display: block;
  margin-top: 2px;
}

/* Delivery Review */
.delivery-review.compact {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-row {
  display: flex;
  gap: 8px;
}

.detail-label {
  font-size: 0.85rem;
  color: #94a3b8;
  min-width: 60px;
  font-weight: 500;
}

.detail-value {
  font-size: 0.85rem;
  color: var(--text-color1);
  flex: 1;
  word-break: break-word;
}

.detail-value.notes {
  font-style: italic;
  color: #cbd5e1;
}

/* Order Summary */
.confirm-pricing {
  background: var(--background-color1);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  border: 1px solid rgba(var(--border-color-hover-rgb), 0.1);
}

.confirm-pricing h3 {
  font-size: 0.95rem;
  font-weight: 600;
  margin: 0 0 12px 0;
  color: var(--text-color1);
  display: flex;
  align-items: center;
  gap: 8px;
}

.price-summary {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.price-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 0.85rem;
  border-bottom: 1px solid rgba(var(--border-color-hover-rgb), 0.1);
}

.price-row:last-child {
  border-bottom: none;
}

.price-row.savings {
  color: #28a745;
  font-weight: 600;
}

.price-row.savings .savings-text {
  color: #28a745;
}

.price-row.after-discount {
  background: rgba(var(--border-color-hover-rgb), 0.05);
  padding: 8px;
  border-radius: 4px;
  margin: 4px 0;
  font-weight: 600;
}

.price-row.total {
  font-size: 1rem;
  font-weight: 700;
  padding-top: 12px;
  border-top: 2px solid var(--border-color-hover);
  margin-top: 8px;
}

/* Actions */
.confirm-actions {
  display: flex;
  gap: 12px;
  padding: 20px;
  border-top: 1px solid rgba(var(--border-color-hover-rgb), 0.1);
  background: var(--background-color2);
}

.c-btn {
  flex: 1;
  padding: 12px 16px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
}

.c-btn i {
  font-size: 0.9rem;
}

.c-btn.cancel {
  background: var(--background-color1);
  color: var(--text-color1);
  border: 1px solid rgba(var(--border-color-hover-rgb), 0.3);
}

.c-btn.cancel:hover:not(:disabled) {
  background: rgba(var(--border-color-hover-rgb), 0.1);
  transform: translateY(-1px);
}

.c-btn.confirm {
  background: var(--border-color-hover);
  color: var(--background-color2);
  border: 1px solid var(--border-color-hover);
}

.c-btn.confirm:hover:not(:disabled) {
  background: rgba(var(--border-color-hover-rgb), 0.9);
  transform: translateY(-1px);
  color: var(--border-color-hover);
  box-shadow: 0 4px 12px rgba(var(--border-color-hover-rgb), 0.2);
}

.c-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
}

/* Success State */
.order-success {
  text-align: center;
  padding: 30px 20px;
}

.success-icon {
  font-size: 3rem;
  color: #28a745;
  margin-bottom: 20px;
}

.order-success h2 {
  font-size: 1.5rem;
  margin-bottom: 15px;
  color: var(--text-color1);
}

.success-message {
  background: rgba(40, 167, 69, 0.1);
  border: 1px solid rgba(40, 167, 69, 0.3);
  border-radius: 8px;
  padding: 15px;
  margin: 20px 0;
  text-align: left;
}

.success-message p {
  margin: 8px 0;
  font-size: 0.9rem;
  color: var(--text-color1);
}

.delivery-time {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(var(--border-color-hover-rgb), 0.1);
  padding: 10px 20px;
  border-radius: 20px;
  margin: 15px 0;
  font-size: 0.9rem;
  color: var(--text-color1);
}

.continue-btn {
  width: 100%;
  padding: 14px;
  background: var(--border-color-hover);
  color: var(--background-color2);
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  margin-top: 15px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.continue-btn:hover {
  background: rgba(var(--border-color-hover-rgb), 0.9);
  transform: translateY(-1px);
}

/* Error State */
.order-error {
  text-align: center;
  padding: 30px 20px;
}

.error-icon {
  font-size: 3rem;
  color: #ef4444;
  margin-bottom: 20px;
}

.order-error h2 {
  font-size: 1.5rem;
  margin-bottom: 15px;
  color: var(--text-color1);
}

.error-message {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  padding: 15px;
  margin: 20px 0;
  text-align: left;
}

.error-message p {
  margin: 8px 0;
  font-size: 0.9rem;
  color: var(--text-color1);
}

.error-actions {
  display: flex;
  gap: 12px;
  margin-top: 25px;
}

.retry-btn {
  flex: 2;
  padding: 12px 16px;
  background: var(--border-color-hover);
  color: var(--background-color2);
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.retry-btn:hover {
  transform: translateY(-1px);
}

.cancel-btn {
  flex: 1;
  padding: 12px 16px;
  background: var(--background-color1);
  color: var(--text-color1);
  border: 1px solid rgba(var(--border-color-hover-rgb), 0.3);
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cancel-btn:hover {
  background: rgba(var(--border-color-hover-rgb), 0.1);
}

/* Animations */
@keyframes modalSlideUp {
  from { 
    transform: translateY(30px) scale(0.98); 
    opacity: 0; 
  }
  to { 
    transform: translateY(0) scale(1); 
    opacity: 1; 
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* ================================
   ULTRA RESPONSIVE BREAKPOINTS - FIXED MOBILE MODAL BUTTONS
   ================================ */

/* Small Mobile (320px - 479px) */
@media (max-width: 479px) {
  .cart-item {
    flex-direction: column;
    align-items: stretch;
    text-align: left;
    padding: clamp(14px, 2vw, 16px);
  }
  
  .product-img-wrapper {
    width: 100%;
    margin-bottom: clamp(12px, 2vw, 16px);
  }
  
  .product-img {
    width: 100%;
    height: 200px;
    min-width: 100%;
  }
  
  .product-info {
    text-align: left;
  }
  
  .product-name,
  .description,
  .price-display {
    text-align: left;
  }
  
  .quantity-wrapper {
    margin-top: clamp(12px, 2vw, 16px);
  }
  
  .quantity {
    justify-content: flex-start;
  }
  
  .item-total-section {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    min-width: 100%;
    margin-top: clamp(12px, 2vw, 16px);
    padding-top: clamp(12px, 2vw, 16px);
    border-top: 2px solid rgba(0,0,0,0.08);
  }
  
  .total-price {
    text-align: left;
  }
  
  /* Modal adjustments for small mobile - FIXED BUTTON VISIBILITY */
  .modal-overlay {
    align-items: center; /* Better bottom alignment for mobile */
  }
  
  .confirm-modal {
    max-height: 100vh; /* Reduced to ensure buttons are visible */
    max-width: 100%;
    border-radius: 12px 12px 0 0;
    margin: 0;
  }
  
  .scrollable-content {
    max-height: calc(85vh - 130px); /* Reduced to make room for buttons */
    padding: 12px;
  }
  
  .confirm-header h2 {
    font-size: 1.2rem;
    gap: 6px;
  }
  
  .confirm-section {
    padding: 12px;
  }
  
  .item-row {
    flex-direction: column;
    gap: 8px;
  }
  
  .item-pricing {
    text-align: left;
    min-width: auto;
    width: 100%;
  }
  
  /* FIXED: Buttons stay on same line with smaller size */
  .confirm-actions {
    flex-direction: row; /* Changed from column to row */
    gap: 8px; /* Reduced gap */
    padding: 12px; /* Reduced padding */
    min-height: auto;
  }
  
  .c-btn {
    width: 100%;
    padding: 10px 12px; /* Smaller padding */
    font-size: 0.8rem; /* Smaller font */
    min-height: 40px; /* Reduced height */
  }
  
  .c-btn i {
    font-size: 0.8rem; /* Smaller icon */
  }
  
  /* Toast adjustments for small mobile */
  .toast-notification {
    top: 10px;
    right: 10px;
    left: 10px;
    padding: 12px;
  }
}

/* Mobile (480px - 767px) */
@media (min-width: 480px) and (max-width: 767px) {
  .cart-item {
    flex-wrap: wrap;
  }
  
  .product-img {
    width: 120px;
    height: 120px;
  }
  
  .product-info {
    min-width: calc(100% - 136px);
  }
  
  .item-total-section {
    width: 100%;
    flex-direction: row;
    justify-content: space-between;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid rgba(0,0,0,0.05);
  }
  
  /* Modal adjustments for mobile - FIXED BUTTON VISIBILITY */
  .modal-overlay {
    padding: 15px;
    align-items: center;
  }
  
  .confirm-modal {
    max-height: 85vh;
    max-width: 95%;
  }
  
  .scrollable-content {
    max-height: calc(85vh - 140px); /* Adjusted for button visibility */
    padding: 15px;
  }
  
  .confirm-header {
    padding: 15px 15px 10px;
  }
  
  .confirm-header h2 {
    font-size: 1.3rem;
  }
  
  .confirm-section {
    padding: 14px;
  }
  
  .confirm-actions {
    padding: 15px;
    flex-direction: row; /* Ensure buttons stay on same line */
    gap: 10px;
  }
  
  .c-btn {
    padding: 10px 14px;
    font-size: 0.85rem;
    min-height: 42px;
  }
}

/* Tablet (768px - 1023px) */
@media (min-width: 768px) and (max-width: 1023px) {
  .cart-item {
    gap: 20px;
  }
  
  .product-img {
    width: 100px;
    height: 100px;
  }
  
  .order-section {
    gap: 24px;
  }
}

/* Desktop (1024px - 1279px) */
@media (min-width: 1024px) and (max-width: 1279px) {
  .cart-section {
    padding-left: 32px;
    padding-right: 32px;
  }
}

/* Large Desktop (1280px+) */
@media (min-width: 1280px) {
  .cart-section {
    padding-left: 40px;
    padding-right: 40px;
  }
}

/* ================================
   ACCESSIBILITY & INTERACTION
   ================================ */
button:focus, 
a:focus, 
input:focus, 
textarea:focus {
  outline: 3px solid rgba(var(--border-color-hover-rgb), 0.2);
  outline-offset: 2px;
  border-radius: 4px;
}

/* Touch device optimizations */
@media (hover: none) and (pointer: coarse) {
  .quantity button,
  .checkout-btn,
  .browse-btn,
  .c-btn,
  .location-mode-btn,
  .live-location-btn {
    min-height: 44px;
    min-width: 44px;
  }
  
  .form-group input,
  .form-group textarea {
    font-size: 16px; /* Prevents iOS zoom */
  }
  
  .cart-item {
    padding: 16px;
  }
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .cart-item,
  .order-summary,
  .delivery-form {
    border: 2px solid currentColor;
  }
  
  button {
    border: 2px solid currentColor;
  }
  
  .confirm-modal {
    border: 2px solid var(--border-color-hover);
  }
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .cart-item,
  .checkout-btn,
  .link-btn,
  .link-btn--primary,
  .browse-btn,
  .quantity button,
  .remove-item,
  .c-btn,
  .location-mode-btn,
  .live-location-btn {
    transition: none !important;
    animation: none !important;
  }

  .link-btn:hover:not(:disabled),
  .link-btn--primary:hover:not(:disabled) {
    transform: none;
  }
  
  .blurred {
    filter: none;
  }
  
  .modal-overlay {
    animation: none;
  }
  
  .confirm-modal {
    animation: none;
  }
  
  .toast-notification {
    animation: none;
  }
  
  .fade-slide-enter-active,
  .fade-slide-leave-active {
    transition: none;
  }
}
</style>